// ════════════════════════════════════════════════════════════
//  artSignatures.js — WHAT each Art and each Lyt Attack looks like
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-20: "just the actual arts, visual fx, and arena"
//  and, on one strike per posture: "yes :3!!!!" (every being gets
//  its own). Composed from the primitives in ArtFx.jsx + the shared
//  ones in Effects.jsx (burst · ring · pillar · scar · punch · shake).
//
//  THREE TABLES, ONE ROW PER LOOK — a new Art or a new being is a new
//  row (or it falls back to the shared look; nothing ever goes blank):
//    SHAPES  · an aimed Art's signature, by its card shape (arts.js `shape`)
//    SELF    · an Art on yourself, by what it DOES (its first effect)
//    STRIKES · a being's Lyt Attack, by line (roster.js `lytName`)
//  The Arts' NUMBERS never live here — this file only decides how a
//  thing looks the moment the engine says it happened.
//
//  Variety is seeded from the event's own seq (the same moment looks
//  the same on every screen watching it), never Math.random().
// ════════════════════════════════════════════════════════════
import { ARTS } from "../data/arts.js";
import { mulberry32 } from "../ieBeingModels.jsx";

const TAU = Math.PI * 2;
const KHAO = "#A855F7", LYF = "#4CC9F0", ANIMA = "#F2B134", LYT = "#FF4D3D", GOLD = "#F2C14E", WHITE = "#ffffff";

// facing helpers: the yaw that puts a slash plane ACROSS a direction, and a point ahead
const ahead = (f, d) => [f.x + Math.sin(f.yaw) * d, f.z + Math.cos(f.yaw) * d];
const toward = (x0, z0, x1, z1) => Math.atan2(x1 - x0, z1 - z0);

// ════════════════════════════════════════════════════════════
//  SHAPES — aimed Arts (target "area"), at (x, z) with radius r
// ════════════════════════════════════════════════════════════
const SHAPES = {
  // three claw rakes torn across the area, then the floor remembers them
  claw(fx, a) {
    const yaw = a.dir + Math.PI / 2;
    for (let i = 0; i < 3; i++) {
      const off = (i - 1) * a.r * 0.32;
      const x = a.x + Math.cos(a.dir) * off, z = a.z - Math.sin(a.dir) * off;
      fx.after(i * 0.06, () => {
        fx.slash({ x, y: 1.3 - a.r * 0.6, z, yaw, roll: -0.55, r: a.r * 0.75, arc: 2.0, width: 0.3, color: a.c, life: 0.42 });
        fx.scar("groove", x, z, { len: a.r * 1.4, r: 0.5, yaw: a.dir + 0.5, color: a.c });
      });
    }
    fx.after(0.2, () => fx.burst(a.x, 0.9, a.z, a.c, a.ult ? 60 : 30, 6, { life: 0.5, size: 0.5, white: 0.4 }));
  },
  // jaws from above and below snap shut on the spot
  fang(fx, a) {
    const yaw = a.dir + Math.PI / 2;
    fx.slash({ x: a.x, y: 0.6, z: a.z, yaw, r: a.r * 0.8, arc: 2.6, width: 0.3, color: a.c, life: 0.35, sweep: 0.4 });
    fx.slash({ x: a.x, y: 2.0, z: a.z, yaw, roll: Math.PI, r: a.r * 0.8, arc: 2.6, width: 0.3, color: a.c, life: 0.35, sweep: 0.4, flip: true });
    for (let i = 0; i < 5; i++) {                                  // the teeth
      const s = (i - 2) * a.r * 0.28;
      const x = a.x + Math.cos(a.dir) * s, z = a.z - Math.sin(a.dir) * s;
      fx.shard({ x, y: 3.0, z, vy: -9, len: 0.55, color: WHITE, life: 0.2, spin: 0, roll: Math.PI });
      fx.shard({ x, y: -0.2, z, vy: 9, len: 0.5, color: a.c, life: 0.2, spin: 0 });
    }
    fx.after(0.2, () => {                                          // CHOMP
      fx.burst(a.x, 1.2, a.z, a.c, a.ult ? 70 : 34, 7, { life: 0.45, size: 0.55, white: 0.5 });
      fx.ring(a.x, a.z, WHITE, { r0: 0.3, r1: a.r, life: 0.3, y: 1.2, vertical: true, yaw: a.dir });
      fx.punch(a.ult ? 5 : 2.5);
      fx.scar("scorch", a.x, a.z, { r: a.r * 0.5 });
      fx.fracture({ x: a.x, z: a.z, r: a.r * 0.8, color: a.c, life: 1.0 });
    });
  },
  // spirits circle in, then dive through everyone inside
  ghost(fx, a) {
    fx.seal({ art: a.art, x: a.x, z: a.z, r0: a.r * 0.4, r1: a.r, color: a.c, spin: 1.6, life: 1.2, opacity: 0.55 });
    for (let i = 0; i < 6; i++) {
      const y = 0.6 + i * 0.35, yaw = a.rnd() * TAU;
      fx.after(i * 0.05, () => fx.slash({ x: a.x, y, z: a.z, yaw, pitch: -Math.PI / 2, r: a.r * (0.9 - i * 0.07), arc: 3.4, width: 0.35, color: i % 2 ? WHITE : a.c, life: 0.7, sweep: 0.75, tail: 0.7 }));
    }
    fx.after(0.55, () => {
      fx.dome({ x: a.x, z: a.z, r0: a.r * 1.1, r1: 0.2, implode: true, color: a.c, life: 0.35, opacity: 0.45 });
      fx.burst(a.x, 0.6, a.z, a.c, a.ult ? 90 : 45, 4, { rise: 2, life: 0.9, size: 0.55, white: 0.3, spread: a.r * 0.5 });
      fx.punch(a.ult ? 4 : 2);
    });
  },
  // ink: a dome of darkness swallows the area, then lets go
  dark(fx, a) {
    fx.blot({ x: a.x, z: a.z, r0: a.r * 0.5, r1: a.r * 1.35, life: 1.4, opacity: 0.85 });
    fx.dome({ x: a.x, z: a.z, r0: 0.3, r1: a.r * 1.05, dark: true, life: 1.1, opacity: 0.88 });
    fx.halo({ x: a.x, y: 0.15, z: a.z, r0: a.r * 0.6, r1: a.r * 1.2, color: a.c, life: 0.8 });
    for (let i = 0; i < 4; i++) fx.after(0.15 + i * 0.1, () => fx.slash({ x: a.x, y: 0.3 + i * 0.5, z: a.z, yaw: a.rnd() * TAU, pitch: -Math.PI / 2, r: a.r * 1.08, arc: 2.2, width: 0.18, color: a.c, life: 0.4 }));
    fx.after(0.9, () => { fx.burst(a.x, 1, a.z, a.c, a.ult ? 90 : 40, 5, { up: 3, life: 0.7, size: 0.5 }); fx.fracture({ x: a.x, z: a.z, r: a.r * 1.1, color: a.c }); fx.punch(a.ult ? 6 : 3); });
  },
  // the scales stand up out of the floor in a ring, edges first
  scale(fx, a) {
    fx.disc({ x: a.x, z: a.z, r0: a.r * 0.6, r1: a.r, color: a.c, tex: "hex", spin: 0.4, life: 1.1, opacity: 0.6 });
    for (let i = 0; i < 12; i++) {
      const t = i / 12 * TAU, rr = a.r * 0.85;
      fx.after(i * 0.03, () => fx.shard({ x: a.x + Math.sin(t) * rr, y: -0.4, z: a.z + Math.cos(t) * rr, vy: 5, g: 9, len: 0.9, color: i % 3 ? a.c : WHITE, life: 0.75, yaw: t, tilt: 0.35, spin: 0 }));
    }
    fx.after(0.35, () => { fx.ring(a.x, a.z, a.c, { r0: a.r, r1: 0.3, life: 0.35 }); fx.burst(a.x, 0.8, a.z, a.c, 30, 4, { life: 0.5, size: 0.45, white: 0.4 }); });
  },
  // it comes in from the sky at an angle and craters the floor
  meteor(fx, a) {
    const n = a.ult ? 3 : 1;
    for (let i = 0; i < n; i++) {
      const ox = i ? (a.rnd() - 0.5) * a.r : 0, oz = i ? (a.rnd() - 0.5) * a.r : 0;
      const x = a.x + ox, z = a.z + oz, back = a.dir + Math.PI;
      fx.after(i * 0.12, () => {
        fx.blot({ x, z, r0: 0.2, r1: a.r * 0.8, life: 0.5, opacity: 0.6 });
        fx.rock({ from: [x + Math.sin(back) * 9, 13, z + Math.cos(back) * 9], to: [x, 0.3, z], r: 0.55 + a.r * 0.1, color: a.c, trail: a.c, life: 0.45,
          onEnd: () => {
            fx.dome({ x, z, r0: 0.3, r1: a.r * 0.9, color: WHITE, life: 0.3, opacity: 0.5 });
            fx.ring(x, z, a.c, { r0: 0.3, r1: a.r * 1.4, life: 0.45 });
            fx.burst(x, 0.4, z, a.c, 50, 8, { up: 4, life: 0.7, size: 0.6, white: 0.35 });
            for (let k = 0; k < 6; k++) { const t = k / 6 * TAU + a.rnd(); fx.shard({ x, y: 0.4, z, vx: Math.sin(t) * 5, vy: 5 + a.rnd() * 3, vz: Math.cos(t) * 5, g: 18, len: 0.35, color: "#3a2a52", life: 0.8 }); }
            fx.scar("crater", x, z, { r: a.r * 0.8, color: a.c });
            fx.fracture({ x, z, r: a.r * 1.3, color: a.c, life: 1.5 });
            fx.shake(0.45, 0.3); fx.punch(4);
          } });
      });
    }
  },
  // the gambler's eyes: dice tumble in and land on the engine's roll
  die(fx, a) {
    const roll = a.dice || [1 + Math.floor(a.rnd() * 6), 1 + Math.floor(a.rnd() * 6)];
    roll.forEach((face, i) => {
      const s = (i ? 1 : -1) * 0.75, x = a.x + Math.cos(a.dir) * s, z = a.z - Math.sin(a.dir) * s;
      fx.die({ x, z, face, yaw: a.rnd() * TAU, life: 1.7, size: 1.0,
        onLand: (lx, lz) => { fx.ring(lx, lz, GOLD, { r0: 0.3, r1: 1.6, life: 0.35 }); fx.burst(lx, 0.3, lz, GOLD, 22, 4, { flat: true, life: 0.45, size: 0.45 }); } });
    });
    fx.seal({ art: a.art, x: a.x, z: a.z, r0: 0.4, r1: a.r, color: a.c, spin: -1.2, life: 1.6, opacity: 0.45 });
    if (roll[0] + roll[1] === 2) fx.after(0.7, () => { fx.pillar(a.x, a.z, "#ff4d8d", { life: 1, w: a.r }); fx.fracture({ x: a.x, z: a.z, r: a.r * 1.5, color: "#ff4d8d", life: 1.6 }); fx.punch(7); fx.shake(0.6, 0.4); });
  },
  // a shadow grows over them — then the foot comes down
  stomp(fx, a) {
    fx.blot({ x: a.x, z: a.z, r0: 0.4, r1: a.r * 1.25, life: 0.75, opacity: 0.85 });
    fx.after(0.5, () => {
      fx.ring(a.x, a.z, WHITE, { r0: 0.3, r1: a.r * 1.8, life: 0.45 });
      fx.ring(a.x, a.z, a.c, { r0: 0.3, r1: a.r * 2.6, life: 0.7 });
      fx.burst(a.x, 0.2, a.z, "#8a78aa", 70, 9, { flat: true, life: 0.7, size: 0.65 });
      for (let k = 0; k < 10; k++) { const t = k / 10 * TAU; fx.shard({ x: a.x + Math.sin(t) * a.r * 0.6, y: 0, z: a.z + Math.cos(t) * a.r * 0.6, vx: Math.sin(t) * 3, vy: 6, vz: Math.cos(t) * 3, g: 20, len: 0.45, color: "#4a3a62", life: 0.7 }); }
      fx.scar("crater", a.x, a.z, { r: a.r * 1.1, color: a.c });
      fx.fracture({ x: a.x, z: a.z, r: a.r * 1.7, color: a.c, life: 1.8 });
      fx.shake(0.8, 0.45); fx.punch(7);
    });
  },
  // a column of light out of the sky, a seal on the floor, rings rising
  light(fx, a) {
    fx.seal({ art: a.art, x: a.x, z: a.z, r0: 0.4, r1: a.r, color: a.c, spin: 0.9, life: 1.3, opacity: 0.7 });
    fx.after(0.1, () => {
      fx.column({ x: a.x, z: a.z, r: Math.max(0.7, a.r * 0.32), h: 20, color: "#ffd98a", life: 1.0, opacity: 0.8 });
      fx.column({ x: a.x, z: a.z, r: Math.max(0.25, a.r * 0.1), h: 20, color: WHITE, life: 0.8, opacity: 0.9 });
      for (let i = 0; i < 3; i++) fx.halo({ x: a.x, y: 0.2 + i * 0.4, z: a.z, r0: a.r * 0.5, r1: a.r * (0.9 - i * 0.15), vy: 2.2, color: GOLD, life: 0.9 });
      fx.burst(a.x, 0.5, a.z, WHITE, a.ult ? 80 : 40, 3, { rise: 5, life: 0.9, size: 0.5, spread: a.r * 0.6 });
      fx.punch(a.ult ? 5 : 2.5);
    });
  },
  // a star falls in on itself — then tears outward
  nova(fx, a) {
    fx.dome({ x: a.x, y: 1, z: a.z, r0: a.r * 1.1, r1: 0.15, implode: true, color: WHITE, life: 0.4, opacity: 0.6, squash: 1 });
    fx.after(0.4, () => {
      fx.dome({ x: a.x, y: 0.2, z: a.z, r0: 0.2, r1: a.r * 1.3, color: a.c, life: 0.6, opacity: 0.55 });
      for (let k = 0; k < 14; k++) { const t = k / 14 * TAU; fx.shard({ x: a.x, y: 1, z: a.z, vx: Math.sin(t) * 11, vy: (a.rnd() - 0.3) * 4, vz: Math.cos(t) * 11, len: 0.6, color: k % 2 ? WHITE : a.c, life: 0.5, spin: 0, yaw: t, tilt: Math.PI / 2 }); }
      fx.scar("crater", a.x, a.z, { r: a.r, color: a.c });
      fx.fracture({ x: a.x, z: a.z, r: a.r * 1.4, color: WHITE, life: 1.5 });
      fx.shake(0.7, 0.4); fx.punch(8);
    });
  },
  // a shower of falling stars across the whole area
  star(fx, a) {
    for (let i = 0; i < 9; i++) {
      const t = a.rnd() * TAU, d = Math.sqrt(a.rnd()) * a.r;
      const x = a.x + Math.sin(t) * d, z = a.z + Math.cos(t) * d;
      fx.after(i * 0.07, () => fx.rock({ from: [x - 2, 11, z - 2], to: [x, 0.2, z], r: 0.22, color: i % 3 ? a.c : WHITE, trail: a.c, core: WHITE, life: 0.35,
        onEnd: () => { fx.ring(x, z, a.c, { r0: 0.2, r1: 1, life: 0.3 }); fx.burst(x, 0.3, z, a.c, 12, 4, { up: 3, life: 0.4, size: 0.4, white: 0.5 }); if (i % 3 === 0) fx.fracture({ x, z, r: 0.9, color: a.c, life: 0.9 }); } }));
    }
    fx.after(0.7, () => fx.punch(a.ult ? 4 : 2));
  },
  // the dream: a spiral opens in the floor and pulls them under
  void(fx, a) {
    fx.disc({ x: a.x, z: a.z, r0: 0.3, r1: a.r * 1.1, color: a.c, tex: "swirl", spin: -4, life: 1.5, opacity: 0.9 });
    fx.blot({ x: a.x, z: a.z, r0: 0.2, r1: a.r * 0.9, life: 1.5, opacity: 0.7 });
    for (let i = 0; i < 8; i++) {                                     // dream bubbles drifting up
      const t = a.rnd() * TAU, d = a.rnd() * a.r;
      fx.after(0.1 + i * 0.08, () => fx.halo({ x: a.x + Math.sin(t) * d, y: 0.3, z: a.z + Math.cos(t) * d, r0: 0.05, r1: 0.25 + a.rnd() * 0.2, vy: 1.5, color: i % 2 ? WHITE : a.c, life: 1.1, tilt: 0 }));
    }
    fx.after(0.6, () => fx.punch(3));
  },
};

// ════════════════════════════════════════════════════════════
//  SELF — Arts on yourself, by what they do (f = the caster)
// ════════════════════════════════════════════════════════════
const SELF = {
  phase(fx, f, c) {                 // Intangible: it slips out of phase
    fx.disc({ x: f.x, z: f.z, r0: 0.3, r1: 1.8, color: "#B388FF", tex: "swirl", spin: 5, life: 0.9, opacity: 0.8 });
    for (let i = 0; i < 3; i++) fx.halo({ x: f.x, y: 0.4 + i * 0.5, z: f.z, r0: 1.4, r1: 0.4, color: "#B388FF", life: 0.6, tilt: Math.PI / 2 });
  },
  thorns(fx, f, c) {                // Reprisal: whatever touches it gets cut
    for (let k = 0; k < 8; k++) { const t = k / 8 * TAU; fx.shard({ x: f.x, y: 0.9, z: f.z, vx: Math.sin(t) * 4, vz: Math.cos(t) * 4, len: 0.5, color: LYT, life: 0.4, yaw: t, tilt: Math.PI / 2, spin: 0 }); }
    fx.slash({ x: f.x, y: 0.9, z: f.z, pitch: -Math.PI / 2, r: 1.5, arc: 6, width: 0.15, color: LYT, life: 0.5, sweep: 0.6 });
  },
  fate(fx, f, c, a) {               // Fate / Destined: a golden halo settles over it
    fx.seal({ art: a.art, x: f.x, z: f.z, r0: 0.4, r1: 1.6, color: c, spin: 0.8, life: 1.4, opacity: 0.7 });
    fx.halo({ x: f.x, y: 3.4, z: f.z, r0: 1.2, r1: 0.45, vy: -0.6, color: GOLD, life: 1.2, tilt: Math.PI / 2 });
    fx.column({ x: f.x, z: f.z, r: 0.35, h: 14, color: GOLD, life: 0.8 });
  },
  coin(fx, f, c, a) {               // Pay Day / Black Cat: the coin goes up, lands on the engine's call
    fx.coin({ x: f.x, z: f.z, y: 2.6, heads: a.heads !== false, life: 1.3, size: 0.42,
      onLand: () => fx.burst(f.x, 2.6, f.z, a.heads !== false ? "#ffe08a" : "#8a7aa0", 18, 3, { life: 0.5, size: 0.4, white: 0.4 }) });
  },
  charge(fx, f, c) {                // Lyt Attacks charged: rings tighten round it, sparks flow in
    for (let i = 0; i < 3; i++) fx.halo({ x: f.x, y: 0.5 + i * 0.6, z: f.z, r0: 1.6, r1: 0.5, color: i === 1 ? WHITE : LYT, life: 0.55, tilt: Math.PI / 2 + (i - 1) * 0.25 });
    for (let k = 0; k < 10; k++) { const t = k / 10 * TAU, d = 2.4; fx.shard({ x: f.x + Math.sin(t) * d, y: 1, z: f.z + Math.cos(t) * d, vx: -Math.sin(t) * 5, vz: -Math.cos(t) * 5, len: 0.35, color: LYT, life: 0.45, yaw: t, tilt: Math.PI / 2, spin: 0 }); }
  },
  draw(fx, f, c, a) {               // Khao drawn in from the field
    fx.seal({ art: a.art, x: f.x, z: f.z, r0: 2.2, r1: 0.6, color: c, spin: -2, life: 0.9, opacity: 0.6 });
    for (let k = 0; k < 12; k++) { const t = k / 12 * TAU, d = 3; fx.shard({ x: f.x + Math.sin(t) * d, y: 0.4 + (k % 3) * 0.5, z: f.z + Math.cos(t) * d, vx: -Math.sin(t) * 5.5, vz: -Math.cos(t) * 5.5, len: 0.3, color: KHAO, life: 0.55, spin: 6 }); }
  },
  shield(fx, f, c) {                // Lyf / Bulwark / Warded / Absorb…: a hex shell closes round it
    fx.dome({ x: f.x, z: f.z, r0: 2.2, r1: 1.3, implode: true, color: LYF, life: 0.9, opacity: 0.75, squash: 1.05 });
    fx.disc({ x: f.x, z: f.z, r0: 0.6, r1: 1.6, color: LYF, tex: "hex", spin: 0.5, life: 1.1, opacity: 0.8 });
    fx.halo({ x: f.x, y: 0.1, z: f.z, r0: 0.3, r1: 1.6, vy: 1.2, color: LYF, life: 0.7, tilt: Math.PI / 2 });
  },
  spear(fx, f, c) {                 // Piercing: its strikes go through — a lance of Lyt out ahead
    fx.slash({ x: f.x, y: 1.1, z: f.z, yaw: f.yaw, pitch: Math.PI / 2, r: 3.0, arc: 0.5, width: 0.12, color: LYT, life: 0.4 });
    fx.shard({ x: f.x, y: 1.1, z: f.z, vx: Math.sin(f.yaw) * 14, vz: Math.cos(f.yaw) * 14, len: 0.9, color: WHITE, life: 0.3, yaw: f.yaw, tilt: Math.PI / 2, spin: 0 });
  },
  light(fx, f, c) {                 // Hope / cleansing: a soft column, rings lifting off
    fx.column({ x: f.x, z: f.z, r: 0.8, h: 16, color: "#fff4c2", life: 0.9 });
    for (let i = 0; i < 2; i++) fx.halo({ x: f.x, y: 0.2 + i * 0.5, z: f.z, r0: 0.6, r1: 1.2, vy: 2, color: GOLD, life: 0.9 });
  },
  blade(fx, f, c) {                 // a Treasure / the Scalpel: a blade forms over it and drives in
    fx.shard({ x: f.x, y: 4.6, z: f.z, vy: -8, len: 1.5, color: c, life: 0.45, spin: 0 });
    fx.after(0.4, () => {
      fx.slash({ x: f.x, y: 1.2, z: f.z, yaw: f.yaw + Math.PI / 2, r: 1.3, arc: 3.4, width: 0.25, color: c, life: 0.4 });
      fx.burst(f.x, 1, f.z, c, 30, 4, { life: 0.5, size: 0.45, white: 0.5 });
    });
  },
  revive(fx, f, c, a) {             // Morning Star / Binary Star: the Necropolis gives one back
    fx.column({ x: f.x, z: f.z, r: 1.0, h: 20, color: WHITE, life: 1.2 });
    fx.seal({ art: a.art, x: f.x, z: f.z, r0: 0.4, r1: 2.0, color: c, spin: 1, life: 1.4, opacity: 0.7 });
    for (let i = 0; i < 3; i++) fx.after(i * 0.15, () => fx.halo({ x: f.x, y: 0.2, z: f.z, r0: 0.4, r1: 1.4, vy: 3, color: i === 1 ? WHITE : GOLD, life: 1.0 }));
  },
  aura(fx, f, c, a) {               // anything else on yourself: a flare of its colour
    fx.seal({ art: a.art, x: f.x, z: f.z, r0: 0.4, r1: 1.8, color: c, spin: 1, life: 0.9, opacity: 0.6 });
    fx.halo({ x: f.x, y: 0.2, z: f.z, r0: 0.4, r1: 1.4, vy: 1.6, color: c, life: 0.8 });
  },
};
const SELF_BY_STATUS = { intangible: "phase", reprisal: "thorns", fate: "fate", destined: "fate", piercing: "spear", hope: "light",
  bulwark: "shield", nullLyt: "shield", warded: "shield", tomeproof: "shield", absorb: "shield", scalpel: "blade" };
const SELF_BY_EFFECT = { coin: "coin", lytCharges: "charge", lyt: "charge", khao: "draw", maxLyf: "shield", reviveIlk: "revive", treasure: "blade", cleanse: "light" };
export function selfLook(artId) {
  const a = ARTS[artId]; if (!a) return "aura";
  const e = a.effects[0] || {};
  if (e.status) return SELF_BY_STATUS[e.status] || "aura";
  for (const k of Object.keys(e)) if (SELF_BY_EFFECT[k]) return SELF_BY_EFFECT[k];
  return "aura";
}

// the field-wide ones (None More Than I, I or None, Monkey Suite…)
function globalLook(fx, f, a) {
  fx.column({ x: f.x, z: f.z, r: 1.2, h: 22, color: a.c, life: 1.2 });
  fx.seal({ art: a.art, x: 0, z: 0, r0: 2, r1: 11, color: a.c, spin: 0.6, life: 1.6, opacity: 0.45 });
  if (a.ruin) { fx.dome({ x: 0, z: 0, r0: 1, r1: 12, dark: true, life: 0.9, opacity: 0.55, squash: 0.5 }); fx.after(0.6, () => fx.fracture({ x: 0, z: 0, r: 10.5, color: a.c, life: 1.8 })); }
  for (let k = 0; k < 16; k++) { const t = k / 16 * TAU; fx.after(0.2 + k * 0.025, () => fx.shard({ x: Math.sin(t) * 10.4, y: -0.5, z: Math.cos(t) * 10.4, vy: 7, g: 10, len: 1.0, color: k % 2 ? WHITE : a.c, life: 0.9, yaw: t, spin: 0 })); }
}

// ════════════════════════════════════════════════════════════
//  STRIKES — each being's Lyt Attack (roster.js lytName in brackets)
//  swing(fx, f, c, rnd) at the moment the lunge/shot goes out
// ════════════════════════════════════════════════════════════
// Slash geometry (ArtFx): a crescent of radius r around its centre, the arc
// centred on the slash's local +Y. With yaw = facing + π/2 the crescent
// stands in the plane of the strike (arc over the top, sweeping back →
// front); with pitch +π/2 it lies flat and bulges FORWARD of its centre.
const UP = Math.PI / 2;
const STRIKES = {
  spectral_tiger(fx, f, c) {        // [Pounce] three teal rakes, diagonal, top to bottom
    for (let i = 0; i < 3; i++) {
      const [x, z] = ahead(f, 0.55 + i * 0.12), s = (i - 1) * 0.3;
      fx.after(i * 0.035, () => fx.slash({ x: x + Math.cos(f.yaw) * s, y: 0.35, z: z - Math.sin(f.yaw) * s, yaw: f.yaw + UP, roll: -0.55, r: 1.25, arc: 1.9, width: 0.34, color: i === 1 ? WHITE : c, life: 0.32 }));
    }
  },
  shadow_fox(fx, f, c) {            // [Shadow Nip] a quick dark bite, amber at the teeth
    const [x, z] = ahead(f, 1.0);
    fx.slash({ x, y: 0.05, z, yaw: f.yaw + UP, r: 0.95, arc: 2.5, width: 0.36, color: "#7a44c8", life: 0.26, sweep: 0.4 });
    fx.slash({ x, y: 2.05, z, yaw: f.yaw + UP, roll: Math.PI, r: 0.95, arc: 2.5, width: 0.36, color: c, life: 0.26, sweep: 0.4, flip: true });
    fx.after(0.1, () => fx.burst(x, 1.05, z, c, 16, 4, { life: 0.35, size: 0.45, white: 0.5 }));
  },
  inverted_serpent(fx, f, c) {      // [Coil Strike] coils wound round the line of the strike
    for (let i = 0; i < 4; i++) {
      const [x, z] = ahead(f, 0.35 + i * 0.4);
      fx.after(i * 0.035, () => fx.slash({ x, y: 1.0, z, yaw: f.yaw, roll: i * TAU / 3, r: 0.8 - i * 0.1, arc: 4.6, width: 0.26, color: i % 2 ? WHITE : c, life: 0.34, sweep: 0.6 }));
    }
  },
  chaos_dragon(fx, f, c) {          // [Ravage] two great burning slashes, crossed (an X facing the foe)
    const [x, z] = ahead(f, 1.1);
    fx.slash({ x, y: 1.1, z, yaw: f.yaw, roll: 0.75, r: 1.5, arc: 2.0, width: 0.34, color: c, life: 0.34 });
    fx.after(0.07, () => fx.slash({ x, y: 1.1, z, yaw: f.yaw, roll: -0.75, r: 1.5, arc: 2.0, width: 0.34, color: "#ff4d3d", life: 0.34, flip: true }));
    fx.after(0.12, () => fx.burst(x, 1.1, z, c, 24, 5, { rise: 3, life: 0.6, size: 0.5, white: 0.2 }));
  },
  lucky_dragon(fx, f, c) {          // [Shade Claw] gold slashes with a violet shade trailing them
    const [x, z] = ahead(f, 0.7);
    fx.slash({ x, y: 0.3, z, yaw: f.yaw + UP, roll: -0.5, r: 1.3, arc: 2.0, width: 0.36, color: KHAO, life: 0.4, tail: 0.85 });
    fx.after(0.04, () => fx.slash({ x, y: 0.3, z, yaw: f.yaw + UP, roll: -0.5, r: 1.15, arc: 2.0, width: 0.26, color: GOLD, life: 0.32 }));
    fx.after(0.12, () => { const [hx, hz] = ahead(f, 1.4); fx.burst(hx, 1.1, hz, GOLD, 12, 5, { life: 0.4, size: 0.38, white: 0.6 }); });
  },
  premier_dragon(fx, f, c) {        // [Bloom Bite] a bite that ends in a bloom of petals
    const [x, z] = ahead(f, 1.0);
    fx.slash({ x, y: 0.05, z, yaw: f.yaw + UP, r: 1.0, arc: 2.5, width: 0.34, color: "#9cffb0", life: 0.28, sweep: 0.4 });
    fx.slash({ x, y: 2.15, z, yaw: f.yaw + UP, roll: Math.PI, r: 1.0, arc: 2.5, width: 0.34, color: c, life: 0.28, sweep: 0.4, flip: true });
    fx.after(0.12, () => { fx.burst(x, 1.1, z, "#ffb3d1", 22, 4, { life: 0.9, size: 0.5, rise: 1, drag: 2 }); fx.burst(x, 1.1, z, c, 14, 3, { life: 0.9, size: 0.42, rise: 1, drag: 2 }); });
  },
  z4z(fx, f, c) {                   // [Gear Strike] a full turning gear-ring cut, facing the foe
    const [x, z] = ahead(f, 1.0);
    fx.slash({ x, y: 1.1, z, yaw: f.yaw, r: 1.05, arc: 5.8, width: 0.2, color: c, life: 0.34, sweep: 0.7, tail: 0.35 });
    fx.slash({ x, y: 1.1, z, yaw: f.yaw, roll: Math.PI, r: 0.7, arc: 5.8, width: 0.16, color: WHITE, life: 0.3, sweep: 0.7, tail: 0.3, flip: true });
    fx.after(0.1, () => fx.burst(x, 1.1, z, c, 14, 6, { life: 0.3, size: 0.32, white: 0.7 }));
  },
  antdroid(fx, f, c) {              // [Mandible] two pincers close from the sides, flat
    fx.slash({ x: f.x, y: 0.8, z: f.z, yaw: f.yaw, pitch: UP, r: 1.6, arc: 1.3, width: 0.32, color: c, life: 0.26, sweep: 0.45 });
    fx.slash({ x: f.x, y: 0.8, z: f.z, yaw: f.yaw, pitch: UP, r: 1.6, arc: 1.3, width: 0.32, color: WHITE, life: 0.26, sweep: 0.45, flip: true });
    fx.after(0.11, () => { const [x, z] = ahead(f, 1.5); fx.burst(x, 0.8, z, c, 16, 5, { life: 0.35, size: 0.42, white: 0.5 }); });
  },
  xulan(fx, f, c) {                 // [Void Lash] one long thin lash across the whole front
    fx.slash({ x: f.x, y: 1.0, z: f.z, yaw: f.yaw, pitch: UP, r: 2.6, arc: 2.4, width: 0.08, color: c, life: 0.38, sweep: 0.5, tail: 0.6 });
    fx.after(0.05, () => fx.slash({ x: f.x, y: 1.05, z: f.z, yaw: f.yaw, pitch: UP, r: 2.5, arc: 2.4, width: 0.05, color: KHAO, life: 0.38, sweep: 0.5, tail: 0.45 }));
  },
  // the three that SHOOT: their muzzle is their signature (the shot itself is Effects' Shots)
  dg0(fx, f, c) {                   // [Halo Bolt] a halo opens at the hand
    const [x, z] = ahead(f, 0.8);
    fx.halo({ x, y: 1.0, z, r0: 0.2, r1: 0.9, color: c, life: 0.3, tilt: 0, yaw: f.yaw });
    fx.halo({ x, y: 1.0, z, r0: 0.1, r1: 0.55, color: GOLD, life: 0.25, tilt: 0, yaw: f.yaw });
  },
  h01(fx, f, c) {                   // [Blue Blaster] a blue muzzle ring and a streak
    const [x, z] = ahead(f, 0.8);
    fx.halo({ x, y: 1.0, z, r0: 0.15, r1: 0.7, color: c, life: 0.22, tilt: 0, yaw: f.yaw });
    fx.slash({ x: f.x, y: 1.0, z: f.z, yaw: f.yaw, pitch: UP, r: 2.2, arc: 0.35, width: 0.12, color: WHITE, life: 0.2, sweep: 0.5 });
  },
  h09(fx, f, c) {                   // [Star Blaster] a five-point star flash
    const [x, z] = ahead(f, 0.8);
    for (let k = 0; k < 5; k++) { const t = k / 5 * TAU; fx.shard({ x, y: 1.0, z, vx: Math.cos(f.yaw) * Math.cos(t) * 3, vy: Math.sin(t) * 3, vz: -Math.sin(f.yaw) * Math.cos(t) * 3, len: 0.4, color: k % 2 ? WHITE : c, life: 0.26, spin: 0, yaw: f.yaw, roll: t }); }
    fx.halo({ x, y: 1.0, z, r0: 0.1, r1: 0.5, color: c, life: 0.2, tilt: 0, yaw: f.yaw });
  },
};
// on the hit itself: what the target's floor remembers
const MARKS = { spectral_tiger: "groove", shadow_fox: "groove", lucky_dragon: "groove", chaos_dragon: "scorch", premier_dragon: "scorch" };

// ════════════════════════════════════════════════════════════
//  THE DOORS — called from BattleScene's pump
// ════════════════════════════════════════════════════════════
// read the engine's own result for a coin or dice Art: it says it as a
// text event in the same tick, right after the art event
function resultOf(b, e) {
  const out = {};
  for (const x of b.events) {
    if (x.seq <= e.seq || x.t !== e.t) continue;
    if (x.type === "art") break;                              // the next Art's words are its own
    if (x.type !== "text" || x.uid !== e.uid || out.heads != null || out.dice) continue;   // only the CASTER's call, the first one
    if (/HEADS/.test(x.text)) out.heads = true;
    if (/TAILS/.test(x.text)) out.heads = false;
    if (/SNAKE EYES/.test(x.text)) out.dice = [1, 1];
    const m = /^(\d) \+ (\d)$/.exec(x.text || ""); if (m) out.dice = [+m[1], +m[2]];
  }
  return out;
}

export function castLook(fx, e, f, color) {
  // every cast: THE ART'S OWN SEAL under the caster for as long as it charges
  // (data/seals.js — rim = its kind/school, the Four = what it uses, heart = whom it touches)
  if (!fx.seal || !f) return;
  const life = Math.max(0.5, (e.delay || 0) + 0.3);
  fx.seal({ art: e.art, x: f.x, z: f.z, r0: 0.5, r1: e.ult ? 2.6 : 1.7, color, spin: e.ult ? 2.2 : 1.1, life, opacity: e.ult ? 0.95 : 0.75 });
  if (e.ult) fx.halo({ x: f.x, y: 0.1, z: f.z, r0: 2.4, r1: 0.6, vy: 1.4, color, life: Math.min(1.2, life) });
}

export function artLook(fx, e, b, f) {
  if (!fx.slash) return false;              // ArtFx not mounted: the shared look stands alone
  const rnd = mulberry32(e.seq * 7919 + 13);
  const c = e.color || WHITE;
  if (e.target === "area" && e.shape && SHAPES[e.shape]) {
    const dir = f ? toward(f.x, f.z, e.x, e.z) : 0;
    SHAPES[e.shape](fx, { x: e.x, z: e.z, r: e.r || 2.5, c, ult: !!e.ult, dir, rnd, art: e.art, ...resultOf(b, e) });
    return true;
  }
  if (!f) return false;
  if (e.target === "global") { const a = ARTS[e.art] || {}; globalLook(fx, f, { c, art: e.art, ruin: (a.effects || []).some((x) => x.ruinOverLyt || x.ruinBenches) }); return true; }
  if (e.target === "self") { const look = selfLook(e.art); SELF[look](fx, f, c, { ...resultOf(b, e), art: e.art }); return true; }
  return false;
}

export function strikeLook(fx, e, f, color) {
  if (!fx.slash || !f || !STRIKES[f.line]) return;
  STRIKES[f.line](fx, f, color || WHITE);
}

export function impactLook(fx, e, by, color) {
  if (!fx.scar || !by) return;
  const mark = MARKS[by.line];
  if (mark === "groove") fx.scar("groove", e.x, e.z, { len: 1.6, r: 0.4, yaw: by.yaw + 0.6, color });
  else if (mark) { fx.scar(mark, e.x, e.z, { r: 0.6 }); if (fx.fracture) fx.fracture({ x: e.x, z: e.z, r: 1.1, color, life: 0.8, opacity: 0.8 }); }
}

export const LOOKS = { shapes: Object.keys(SHAPES), self: Object.keys(SELF), strikes: Object.keys(STRIKES) };
